/**
 * **Le Fil** — la file de commentaires, son arrivée et son vieillissement. ⟦SPEC-FIL §3⟧, M3.20.
 *
 * ── Ce que ce module fait, et ce qu'il ne fait pas ────────────────────────────
 *
 * Il **fait arriver** des commentaires et les **fait vieillir**. Il ne les traite pas : les cinq
 * réponses (cœur, humour, épingler, ignorer, supprimer) sont **M3.21**, et l'action
 * `lire_commentaires` ne gagnera sa charge utile `traitements?` qu'à ce moment-là.
 *
 * ⚠️ **Personne ne l'appelle encore.** `resolve` ne branche ni l'arrivée ni le vieillissement :
 * c'est le premier geste de M3.21, et il est délibérément séparé de l'écriture du socle. Un
 * module éteint et non branché ne peut rien casser ; un module branché à moitié, si.
 *
 * ── L'interrupteur, et pourquoi il est la première ligne de chaque fonction ───
 *
 * ⟦SPEC-FIL §10 · A-10⟧ Le Fil est livré **ÉTEINT** (`content/meta.json → fil_actif: false`),
 * donc **rien de ce module ne s'exécute** et une partie se comporte **exactement** comme avant —
 * pas « à peu près » : le flux de RNG lui-même n'est pas touché, puisqu'aucun `draw` n'est
 * consommé. C'est ce qui permet de poser un système de 58 promesses sans qu'une seule ancre ni
 * une seule cible de CI ⟦A-40⟧ ne bouge.
 *
 * ⚠️ Corollaire à ne pas perdre de vue quand on l'allumera : **allumer le Fil déplacera les
 * tirages**, donc toutes les mesures. L'avant/après devra se faire à interrupteur constant.
 *
 * ── Pureté (CLAUDE.md) ───────────────────────────────────────────────────────
 *
 * Aucune I/O, aucune horloge système, aucun `Math.random`. Tout aléa passe par
 * `draw(seed, turn, purpose, index)` sur les deux purposes déclarés en ⟦A-57⟧ : `fil_draw`
 * (quels commentaires arrivent, un tirage par commentaire) et `fil_outcome` (le jet d'humour,
 * en M3.21). La liste de SPEC-MOTEUR §3.2 est **fermée** : l'étendre est un acte de spec, il a
 * été fait, et il s'arrête à deux valeurs.
 */
import { H_JOUR, H_MOIS, H_SEMAINE, H_SILENCE_TOLERE } from "./clock.js";
import { flagPose } from "./events.js";
import { estFantome, extinctionTelegraphiee } from "./gameover.js";
import { draw, pickWeighted } from "./rng.js";
import { appliquerTraitJauge } from "./traits.js";
import { InvalidAction } from "./types.js";
// ─────────────────────────────────────────────────────────────────────────────
// Constantes ⟦§12⟧ — toutes ✎, aucune n'est calibrée tant que le Fil est éteint
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§3.2⟧ **En dessous de 50 abonnés, personne ne commente.** Ce n'est pas un verrou de game
 * design, c'est la vérité du métier : une chaîne à trois abonnés n'a pas de section
 * commentaires. Il place naturellement la découverte du Fil dans l'**arc 2** ⟦A-9⟧, alors que
 * l'escalier ⟦A-5⟧ ouvre `lire_commentaires` dès la 3ᵉ publication — entre les deux, l'action
 * garde son comportement d'aujourd'hui ⟦§6.3, régime « legacy »⟧.
 */
export const FIL_ABONNES_MIN = 50;
/** ⟦§3.2⟧ Socle de l'arrivée — PLAN §4.14 dit « 3-5 ». ✎ */
export const FIL_ARRIVEE_BASE = 3;
/** ⟦§3.2⟧ Jamais plus de 5 commentaires d'un coup (PLAN §4.14). */
export const FIL_ARRIVEE_MAX = 5;
/** ⟦§3.2⟧ +1 commentaire par arrivée au-delà de ce palier. ✎ */
export const SEUIL_ABONNES_ARRIVEE = 1_000;
/** ⟦§3.2⟧ +1 commentaire par arrivée quand la communauté chauffe. ✎ */
export const SEUIL_HYPE_ARRIVEE = 60;
/**
 * ⟦§3.4⟧ Durée de vie d'un commentaire non lu : **une semaine simulée**.
 *
 * ⚠️ **Écart assumé avec le chiffre de SPEC-FIL, et il faut le lire.** La spec écrit
 * `FIL_PEREMPTION_H (112 ✎)` **en le nommant « une semaine simulée »**. Les deux ne peuvent plus
 * être vrais en même temps depuis ⟦A-19⟧ : la journée fait 24 h, donc une semaine en fait 168,
 * et 112 n'en vaut plus que 4,7 jours. A-19 est postérieure et normative — *« toute durée de
 * règle se dérive de `H_JOUR`, jamais écrite en heures absolues »* (`clock.ts`) — et c'est
 * exactement la dérive silencieuse qu'elle demande de traquer. On garde donc la **règle**
 * (une semaine) plutôt que son arithmétique périmée. Consigné en ⟦A-57⟧ ; la valeur reste ✎.
 */
export const FIL_PEREMPTION_H = H_SEMAINE; // 168 h
/**
 * ⟦§3.4⟧ Taille maximale du Fil. Au-delà, **les plus anciens sortent d'abord**.
 *
 * Le plafond n'est pas qu'un choix de rythme : l'état est cloné à chaque action ⟦A38⟧ et
 * sérialisé en base — le Fil ne doit **jamais** croître avec la durée de la run.
 */
export const FIL_TAILLE_MAX = 12;
/**
 * ⟦§4⟧ Commentaires traitables en une action `lire_commentaires`. La boucle du Fil doit rester
 * « 20-30 secondes par session » (PLAN §4.14). Le nœud *Réponses aux commentaires* le passera à
 * 4 en M3.21 — traiter plus vite, pas mieux.
 */
export const FIL_LOT = 3;
// — §3.3 La coloration du tirage par l'image de chaîne (PLAN §4.7 : « audience chaos ») —
export const SEUIL_IMAGE_LUMIERE = 20;
export const SEUIL_IMAGE_CHAOS = -20;
export const SEUIL_IMAGE_CHAOS_PROFOND = -40;
export const MOD_IMAGE_LUMIERE_DUR = 0.6; // ✎ troll / toxique quand l'image est lumineuse
export const MOD_IMAGE_LUMIERE_SYMPA = 1.3; // ✎
export const MOD_IMAGE_CHAOS_DUR = 1.5; // ✎ image ≤ −20
export const MOD_IMAGE_CHAOS_SYMPA = 0.8; // ✎
export const MOD_IMAGE_CHAOS_PROFOND_DUR = 2; // ✎ image ≤ −40, remplace le ×1,5
export const MOD_IMAGE_CHAOS_PROFOND_SYMPA = 0.6; // ✎
/** ⟦§3.3⟧ Les stars viennent quand ça chauffe : ×2 sur les `star` au-delà de ce seuil. ✎ */
export const MOD_HYPE_STAR = 2;
export const SEUIL_HYPE_STAR = 70;
/**
 * ⟦§7 · A-4 §2⟧ **« Le Fil est plus rude »** — la créatrice reçoit +50 % de commentaires
 * `troll` et `toxique`. Le trait mord ici, dans le **poids de tirage**, et nulle part ailleurs.
 *
 * 🔴 **RÈGLE DE NON-CUMUL, la plus importante du module.** Aujourd'hui, faute de Fil, le trait
 * mord sur le **coût forfaitaire** de `lire_commentaires` (`traits.coutCommentaires()`).
 * `coutCommentaires` DOIT disparaître **dans le même commit** que la mise en service de ce
 * modificateur — c'est-à-dire en M3.21, quand `resolve` appellera enfin ce module. Garder les
 * deux ferait payer le trait deux fois (plus de toxiques **et** chacun 50 % plus cher), et le
 * déséquilibre serait **invisible** : deux lignes, deux fichiers, chacune justifiable seule.
 *
 * Tant que `fil_actif` vaut `false`, aucun commentaire n'est tiré : ce modificateur est **inerte**
 * et il n'y a pas de double comptage. Allumer le Fil sans avoir retiré `coutCommentaires` en
 * créerait un — c'est la seule chose à ne pas faire.
 */
export const MULT_TOXIQUES_F = 1.5;
/** Les deux tonalités que le trait genré ⟦A-4 §2⟧ et l'image de chaîne rendent plus fréquentes. */
const TONALITES_DURES = ["troll", "toxique"];
// ─────────────────────────────────────────────────────────────────────────────
// §4 — Ce que les cinq réponses coûtent et rapportent. Toutes ✎ ⟦§12⟧.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§4.1 · A-113 §2⟧ Coût de santé mentale de la tonalité `toxique`, **quelle que soit la
 * réponse**. La **seule** tonalité (avec `troll`) qui fait vraiment mal.
 *
 * Décision de Mathieu (25/08/2026) : *« On ne va pas appuyer sur un bouton "lire". Tu es
 * obligé de le lire pour le supprimer, pour répondre, pour ne rien faire — pour tout. »* Ce
 * chiffre **REMPLACE le barème d'⟦A-102 §1 / A-111 §1⟧**, qui faisait varier le prix avec le
 * GESTE plutôt qu'avec la LECTURE : `coeur`/`epingler` sur un dur payaient le double (le
 * « risque » de PLAN §4.14), `supprimer` payait la moitié (`RATIO_SM_SUPPRESSION`, retiré).
 * Les deux existaient pour punir ou récompenser une réponse ; ⟦A-113 §2⟧ efface l'idée même —
 * voir `coutSMCommentaire` pour le calcul, et l'avenant pour la mesure appariée.
 */
export const SM_TOXIQUE = -1.5;
/**
 * ⟦§4.1 · A-113 §2⟧ Coût de santé mentale de `troll`, **quelle que soit la réponse** — il
 * cherche la réaction, pas la nuisance. Même remplacement que `SM_TOXIQUE` ci-dessus.
 */
export const SM_TROLL = -0.5;
/**
 * ⟦§4.1 · PLAN §4.7⟧ Sous cette image, « lire les commentaires coûte double ». C'est le **même
 * seuil** que celui qui double le forfait de `lire_commentaires` (`actions.ts`) et que celui de
 * la coloration profonde du tirage (`SEUIL_IMAGE_CHAOS_PROFOND`) — une seule frontière du chaos.
 */
export const SEUIL_IMAGE_DOUBLE_PEINE = SEUIL_IMAGE_CHAOS_PROFOND;
/**
 * ⟦§4.1 · PLAN §4.14⟧ Le nœud 💬 *Gestion de crise* : « les toxiques ne coûtent plus de SM ».
 *
 * ⚠️ **Recopié depuis `jalons.ts`, à dessein.** `NOEUD_GESTION_DE_CRISE` y est déjà exporté,
 * mais `jalons.ts` importe `effects.ts`, qui importe `fil.ts` depuis M3.22 (`gauge_delta` sur
 * `hype`) : la flèche fermerait un cycle d'initialisation. C'est le même arbitrage que
 * `NOEUD_FIGURE_PUBLIQUE` (`pause.ts` l'importe d'ici) — chaque module nomme le nœud
 * qu'il lit. Le test T-FIL-13 vérifie que les deux constantes désignent le même nœud existant.
 */
export const NOEUD_GESTION_DE_CRISE_FIL = "gestion_de_crise";
/**
 * ⟦§4.2 · §9.5 · A-82⟧ **Cœur sur vous** : les `coeur` ont **+50 % d'effet**. Une CAPACITÉ,
 * débloquée par un nœud existant (*Le live*, `content/skills.json`) — jamais un id de nœud.
 *
 * 🔑 Pourquoi pas un nœud `coeur_sur_vous` : l'arbre est FERMÉ à 39 nœuds / 195 PC ⟦A-21⟧, et
 * le moteur testait `state.skills.includes("coeur_sur_vous")` — une condition qui ne pouvait
 * **jamais** être vraie (`acheter_skill` valide contre le contenu). Le patron ⟦A-56⟧ : l'effet
 * entre par un nœud existant qui porte déjà un confort voisin, et le moteur lit la capacité.
 * Quel nœud la porte appartient au contenu (CLAUDE.md : contenu = données).
 */
export const CAPACITE_COEUR_SUR_VOUS = "coeur_sur_vous";
/** ⟦§4.2 · PLAN §4.14⟧ « +50 % d'effet » sur la réponse `coeur`. */
export const MULT_COEUR_SUR_VOUS = 1.5;
/**
 * ⟦A-82⟧ **Une capacité est-elle débloquée par un nœud possédé ?** Lecture directe du contenu :
 * les `effets` `unlock` des nœuds de `state.skills`.
 *
 * ⚠️ `collectModifiers` (`effects.ts`) sait déjà le faire — mais `effects.ts` importe ce module
 * (`clampHype`, M3.22), et la flèche inverse fermerait un cycle. Le même arbitrage que
 * `noeudsCharisme` juste en dessous : on lit `content.skills`, on ne recopie aucune liste. Les
 * objets de boutique ne portent pas d'`unlock` de Fil (T16 : une capacité, un nœud).
 */
export function capaciteDeLArbre(state, content, capacite) {
    const possedes = new Set(state.skills);
    for (const s of content.skills ?? []) {
        if (!possedes.has(s.id))
            continue;
        for (const e of s.effets) {
            const effet = e;
            if (effet.type === "unlock" && effet.capability === capacite)
                return true;
        }
    }
    return false;
}
/** ⟦§9.5⟧ Le nœud 💬 *Réponses aux commentaires* : le lot passe de 3 à 4 — plus vite, pas mieux. ✎ */
export const NOEUD_REPONSES_COMMENTAIRES = "reponses_commentaires";
// — §9.5 Le community manager salarié : la délégation du Fil ⟦A-82⟧ —
/**
 * ⟦§9.5 · A-82⟧ La capacité du **community manager**, portée par un nœud EXISTANT de la branche
 * Business (*Le Studio*, `content/skills.json`) — le même patron que *Cœur sur vous*. Après le
 * Grand Saut par construction ⟦A-29⟧ : c'est de l'argent, et le nœud coûte des Clicks.
 */
export const CAPACITE_COMMUNITY_MANAGER = "community_manager";
/** ⟦§9.5⟧ Son salaire par **semaine simulée**, débité pro rata à chaque nuit. ✎ */
export const CM_COUT_HEBDO = 40;
/** ⟦§9.5⟧ Commentaires qu'il traite par nuit, en `coeur`. ✎ */
export const CM_TRAITEMENT = 2;
/** ⟦§9.5⟧ « Effet ÷2 » — la délégation, encore : payante, correcte, jamais aussi bonne que toi. ✎ */
export const CM_DIV_EFFET = 2;
/**
 * Ce qu'il a le droit de toucher : **la routine**, et rien d'autre. Jamais une `star` (c'est
 * ton moment), jamais un `troll` / `toxique` (il n'est pas payé pour encaisser à ta place),
 * jamais un commentaire qui porte un cadeau (une idée est pour ta main, une amorce d'événement
 * est une DÉCISION — contrainte de Mathieu du 18/08 : rien n'automatise ce qui se décide),
 * jamais l'épinglé. Ce qui compte reste au joueur.
 */
const TONALITES_ROUTINE = ["sympa", "question", "critique"];
/** ⟦§4.3⟧ La branche dont chaque nœud possédé améliore le jet d'humour. */
export const BRANCHE_CHARISME = "charisme";
/** ⟦§4.2⟧ Fidélité d'un `coeur`. ✎ */
export const FID_COEUR = 0.5;
/** ⟦§4.4⟧ Fidélité d'une épingle **positive** (`sympa` / `star`). ✎ */
export const FID_EPINGLE = 0.5;
/** ⟦§4.3⟧ « Tu t'es moqué d'un fan » : ce que la vanne ratée coûte à la Fidélité. ✎ */
export const FID_HUMOUR_RATE = -2;
/** ⟦§4.3⟧ Ce que la vanne ratée coûte à la santé mentale. ✎ */
export const SM_HUMOUR_RATE = -2;
/** ⟦§4.3 · A33⟧ Une vanne qui passe pousse l'image d'un point vers le chaos assumé. ✎ */
export const IMAGE_HUMOUR_REUSSI = 1;
/** ⟦§4.3 · A33⟧ Une vanne qui rate en coûte deux — la dérive chaos non choisie. ✎ */
export const IMAGE_HUMOUR_RATE = -2;
/** ⟦§4.3⟧ Probabilité de base qu'une vanne passe. ✎ */
export const P_HUMOUR_BASE = 0.55;
/** ⟦§4.3⟧ Ce que chaque nœud 💬 Charisme possédé ajoute au jet. ✎ */
export const P_HUMOUR_PAR_NOEUD = 0.05;
/** ⟦§4.3⟧ Zone rouge : on rate ses vannes quand on va mal. ✎ */
export const P_HUMOUR_MALUS_ZONE_ROUGE = -0.1;
/** ⟦§4.3⟧ Seuil de la zone rouge de santé mentale (§11.1 : « SM ≤ 29 »). */
export const SM_ZONE_ROUGE = 29;
/** ⟦§4.3⟧ Une vanne ne passe jamais à coup sûr. ✎ */
export const P_HUMOUR_MAX = 0.9;
/**
 * ⟦§4.5⟧ **Le malus d'abandon** — trois conditions cumulées, impossible à déclencher par
 * accident : le Fil quasi plein, aucune lecture, et plus de trois nuits d'affilée.
 */
export const FIL_SEUIL_ABANDON = 8;
/** ⟦§4.5⟧ Nuits d'affilée tolérées avant que la Fidélité ne paie. ✎ */
export const FIL_NUITS_TOLEREES = 3;
/** ⟦§4.5⟧ Fidélité perdue **par nuit** au-delà de la tolérance — pas de pente cumulative. ✎ */
export const FIL_MALUS_ABANDON = 0.5;
/** ⟦§4.6⟧ Suppressions tolérées sur la fenêtre d'abus avant que `fil_censure` ne se pose. ✎ */
export const FIL_SUPPRESSIONS_TOLEREES = 5;
/**
 * ⟦§4.6⟧ La fenêtre d'abus de censure : **trente jours simulés**.
 *
 * ⚠️ **Même écart assumé que `FIL_PEREMPTION_H`** ⟦A-57 §2⟧ : SPEC-FIL écrit
 * `FIL_FENETRE_ABUS_H = 480 h` **en le nommant « 30 jours simulés »**, et les deux ont cessé
 * d'être vrais ensemble avec ⟦A-19⟧ (la journée fait 24 h, donc trente jours en font 720). On
 * garde la **règle**, pas son arithmétique périmée, et on l'écrit comme une dérivée de `H_JOUR`
 * — `H_MOIS`, celle-là même que la faillite et les strikes utilisent déjà.
 */
export const H_FIL_FENETRE_ABUS = H_MOIS; // 720 h
/**
 * ⟦§4.6⟧ Supprimer un commentaire qui n'est **ni** `troll` **ni** `toxique` compte double dans
 * le compteur d'abus. Supprimer une critique légitime, c'est ça, la censure. ✎
 */
export const POIDS_SUPPRESSION_ABUSIVE = 2;
/**
 * ⟦§4.6 · A-102 §2⟧ **Supprimer un `troll` ou un `toxique` ne pèse plus RIEN dans la fenêtre
 * d'abus.** Décision de Mathieu (23/08/2026) : *« Cette censure-là, personne ne la voit et
 * tout le monde s'en fiche. »* Avant cet avenant, la suppression d'un dur comptait 1 (contre 2
 * pour un commentaire légitime) — un poids non nul, donc un abus « invisible » pouvait quand
 * même faire avancer le compteur. Il n'avance plus que sur ce que la communauté remarque
 * vraiment : la disparition d'une critique ou d'une question, jamais celle d'un troll ou d'un
 * toxique.
 */
export const POIDS_SUPPRESSION_TROLL_TOXIQUE = 0;
/**
 * ⟦§5.1⟧ Décalage d'index du tirage `inspiration_draw` d'un cadeau `idee`, pour ne **jamais**
 * collisionner avec la carte de base du même tour (index 0) ni avec la veille. ✎
 */
export const INDEX_CADEAU_IDEE = 10;
/** ⟦§4.3 · PLAN §4.14⟧ Ce que le clip viral d'une vanne ajoute au jet viral suivant. ✎ */
export const CLIP_VIRAL_P_BONUS = 0.01;
// ─────────────────────────────────────────────────────────────────────────────
// §2.2 / §2.4 — Ce qui fait monter la Hype, et ce qui la fait redescendre
// ─────────────────────────────────────────────────────────────────────────────
/** ⟦§2.1⟧ Valeur initiale d'une run. **Zéro, et zéro ne pénalise jamais** (§2.3). */
export const HYPE_INITIALE = 0;
/** ⟦§2.2⟧ Réponse `coeur`. ✎ — ×1,5 avec *Cœur sur vous* (M3.21). */
export const HYPE_COEUR = 2;
/** ⟦§2.2⟧ Vanne réussie. ✎ */
export const HYPE_HUMOUR_REUSSI = 5;
/** ⟦§2.2⟧ Vanne ratée : « tu t'es moqué d'un fan ». ✎ */
export const HYPE_HUMOUR_RATE = -3;
/** ⟦§2.2 · §4.4⟧ Épingler un `sympa` / `star`. ✎ */
export const HYPE_EPINGLE_POSITIVE = 3;
/** ⟦§2.2 · §4.4⟧ Épingler un `troll` / `toxique`, c'est amplifier le troll. ✎ */
export const HYPE_EPINGLE_NEGATIVE = -4;
/** ⟦§2.2 · §4.1⟧ Traiter un commentaire `star` autrement qu'en l'ignorant. ✎ */
export const HYPE_STAR = 4;
/** ⟦§2.2⟧ Le simple fait de sortir une vidéo. ✎ */
export const HYPE_PUBLICATION = 3;
/** ⟦§2.2⟧ Jet viral réussi (SPEC-MOTEUR §10.2). ✎ */
export const HYPE_VIRAL = 20;
/** ⟦§2.2⟧ Vidéo jalon réussie. ✎ */
export const HYPE_JALON = 10;
/**
 * ⟦§2.3 A⟧ **Le bonus de Portée maximal de la Hype — 0,05 à 100 de Hype.** ✎
 *
 * C'est le **seul** effet mécanique de la jauge (§2.3 C) : ni la conversion, ni la qualité, ni
 * les revenus, ni les poids d'événements. Un seul point d'entrée dans la formule = un seul
 * endroit à débrancher si l'équilibrage le demande.
 */
export const BONUS_PORTEE_HYPE_MAX = 0.05;
/**
 * ⟦§2.3 B⟧ **Le seuil de la vague de partage** — PLAN §4.14 : « ≥ 70, ta communauté partage ta
 * prochaine vidéo ». ✎
 */
export const SEUIL_VAGUE = 70;
/**
 * ⟦§2.3 B⟧ Multiplicateur de la **première tranche** de la courbe de rendement (§6.2, le J+1).
 * **Non ✎** : c'est très exactement le « +20 % de vues sur les premières 24 h simulées » de
 * PLAN §4.14, la seule valeur de tout le Fil que le PLAN fixe lui-même.
 */
export const MULT_VAGUE = 1.2;
/**
 * ⟦§2.3 B⟧ Ce que la vague **coûte** en points de Hype. ✎
 *
 * Sans cette ligne, un joueur au-dessus de 70 achète un ×1,2 **permanent** et la Hype cesse
 * d'être une jauge pour devenir un palier. Avec elle, une communauté qu'on fait partager se
 * dépense : c'est la question n° 3 de SPEC-FIL §13, et le défaut retenu est « oui, ça coûte ».
 */
export const HYPE_COUT_VAGUE = 15;
/**
 * ⟦§2.4⟧ Points de Hype perdus par **jour simulé** de silence relatif. ✎
 *
 * C'est la **cinquième pression** d'⟦A-15⟧ — le monde continue sans toi — et elle a la même
 * forme que les quatre autres : elle **se voit venir** (jauge affichée, ligne de récapitulatif
 * ⟦A-16⟧), elle ne surprend jamais.
 */
export const HYPE_DECAY_JOUR = 2;
/**
 * ⟦§2.4⟧ Silence toléré avant que la décroissance ne double. **Trois jours simulés**, la même
 * valeur que le momentum (`H_SILENCE_TOLERE`) et pour la même raison : ⟦A-15 §5⟧ dit
 * explicitement *« le momentum tolère 3 jours de silence : s'en inspirer »*. Écrite comme une
 * référence et non comme un nombre, elle suivra `H_JOUR` si la journée change ⟦A-19⟧.
 */
export const H_HYPE_TOLERANCE_SILENCE = H_SILENCE_TOLERE; // 72 h
/** ⟦§2.4⟧ Au-delà de la tolérance, le silence coûte double. ✎ */
export const MULT_DECAY_SILENCE = 2;
/** ⟦§2.4 · PLAN §4.14⟧ Le nœud *Figure publique* divise la décroissance par deux. */
export const DIV_DECAY_FIGURE_PUBLIQUE = 2;
/** Id du nœud 💬 Charisme qui protège la Hype (`content/skills.json`). */
export const NOEUD_FIGURE_PUBLIQUE = "figure_publique";
// ─────────────────────────────────────────────────────────────────────────────
// L'interrupteur ⟦§10⟧
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§10⟧ Le Fil est-il allumé ? **Absent ⇔ éteint** — c'est l'état neutre, et c'est le défaut.
 *
 * Lu à chaque appel plutôt que mémorisé : le contenu peut changer entre deux parties, et une
 * valeur figée au bootstrap survivrait à un rechargement de contenu. Le coût est nul.
 */
export function filActif(content) {
    return content.meta?.fil_actif === true;
}
// ─────────────────────────────────────────────────────────────────────────────
// Lectures d'état — ne jamais lire `state.hype` ni `state.fil` en direct
// ─────────────────────────────────────────────────────────────────────────────
/** ⟦§2.1⟧ La Hype de la run. `undefined` ⇔ **0** — pas « inconnu » : zéro. */
export function hype(state) {
    return clampHype(state.hype ?? 0);
}
/** Borne la Hype dans `[0 ; 100]` ⟦§2.1⟧. */
export function clampHype(valeur) {
    return Math.min(100, Math.max(0, valeur));
}
/** ⟦§3⟧ La file, jamais `undefined` — un Fil absent est un Fil vide. */
export function fil(state) {
    return state.fil ?? [];
}
/** Les commentaires du contenu, jamais `undefined` — une liste vide est un contenu valide. */
export function vivierCommentaires(content) {
    return content.comments ?? [];
}
// ─────────────────────────────────────────────────────────────────────────────
// §3.5 — Le Fil se vide : le télégraphiage du Fantôme
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§3.5⟧ **Le Fil ne décide de rien** : il ne fait que *montrer* un état que le moteur calcule
 * déjà. C'est le seul endroit où il sert de signal de fin, et aucune condition de fin ne dépend
 * de lui.
 *
 *  - mi-chemin d'une voie du Fantôme ⟦A-58⟧ → **1** commentaire par arrivée : le Fil s'assèche,
 *    il ne meurt pas ;
 *  - fin atteinte → **0** : plus personne.
 *
 * ⚠️ Les seuils ne sont **pas recopiés ici** — ils sont lus chez `gameover.ts`
 * (`extinctionTelegraphiee`, `estFantome`). Deux jeux de constantes pour la même règle
 * divergeraient le jour où l'une bouge, et le Fil mentirait sur l'état du jeu.
 * Rend `null` quand l'extinction n'est pas en cours : l'arrivée normale s'applique.
 */
export function plafondExtinction(state, silenceH = 0) {
    if (estFantome(state) || state.status === "game_over")
        return 0;
    if (extinctionTelegraphiee(state, silenceH))
        return 1;
    return null;
}
// ─────────────────────────────────────────────────────────────────────────────
// §3.2 — Combien de commentaires arrivent
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§3.2⟧ Le nombre de commentaires d'une arrivée. **Aucun tirage** : c'est une lecture de
 * l'état, pas un hasard — le joueur doit pouvoir prévoir que sa communauté parle plus quand
 * elle est plus grande et plus chaude.
 *
 * ```
 * si subscribers < FIL_ABONNES_MIN               → 0
 * base = 3 (+1 si ≥ 1 000 abonnés) (+1 si hype ≥ 60 OU dernière publication virale)
 * clamp(base ; 0 ; 5), puis EXTINCTION (§3.5) : forcé à 1, ou à 0
 * ```
 */
export function nbCommentairesALArrivee(state, ctx) {
    if (state.subscribers < FIL_ABONNES_MIN)
        return 0;
    let n = FIL_ARRIVEE_BASE;
    if (state.subscribers >= SEUIL_ABONNES_ARRIVEE)
        n += 1;
    if (hype(state) >= SEUIL_HYPE_ARRIVEE || ctx.virale === true)
        n += 1;
    n = Math.min(FIL_ARRIVEE_MAX, Math.max(0, n));
    const plafond = plafondExtinction(state, ctx.silenceH ?? 0);
    return plafond === null ? n : Math.min(n, plafond);
}
// ─────────────────────────────────────────────────────────────────────────────
// §3.3 — L'éligibilité et les poids
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§3.3 · §8.2⟧ Ce commentaire peut-il arriver **maintenant**, dans cet état ?
 *
 * Même forme que les conditions d'événement, et pour la même raison : une condition absente
 * n'exclut rien. Un vivier dont **aucune** entrée ne passe rend un Fil vide — ce n'est pas une
 * erreur, c'est un contenu qui ne parle pas de cette situation.
 */
export function commentaireEligible(c, state, ctx) {
    const k = c.conditions;
    if (k.subs_min !== undefined && state.subscribers < k.subs_min)
        return false;
    if (k.subs_max !== undefined && state.subscribers > k.subs_max)
        return false;
    if (k.image_min !== undefined && state.channel_image < k.image_min)
        return false;
    if (k.image_max !== undefined && state.channel_image > k.image_max)
        return false;
    const h = hype(state);
    if (k.hype_min !== undefined && h < k.hype_min)
        return false;
    if (k.hype_max !== undefined && h > k.hype_max)
        return false;
    // Les trois conditions de vidéo ne peuvent passer qu'à une arrivée de publication : à la
    // nuit, il n'y a pas de vidéo dont parler.
    if (k.apres_publication === true && !ctx.publication)
        return false;
    if (k.classe_video !== undefined && (!ctx.publication || ctx.classe !== k.classe_video)) {
        return false;
    }
    if (k.format_video !== undefined && (!ctx.publication || ctx.format !== k.format_video)) {
        return false;
    }
    // ⟦Arbitrage A28⟧ `flagPose` lit les TROIS registres — flags moteur dérivés, `state.flags`,
    // flags de contenu datés. N'en lire qu'un est un bug : `content/` s'appuie sur les trois.
    for (const f of k.flags_requis)
        if (!flagPose(state, f))
            return false;
    for (const f of k.flags_interdits)
        if (flagPose(state, f))
            return false;
    return true;
}
/**
 * ⟦§3.3⟧ **Le Fil se colore par l'alignement** (PLAN §4.14 : « audience chaos = plus de
 * toxiques »). Une chaîne lumineuse reçoit plus de gentillesse, une chaîne chaos reçoit ce
 * qu'elle a appelé.
 */
export function modImage(tonalite, image) {
    const dur = TONALITES_DURES.includes(tonalite);
    if (!dur && tonalite !== "sympa")
        return 1;
    if (image >= SEUIL_IMAGE_LUMIERE)
        return dur ? MOD_IMAGE_LUMIERE_DUR : MOD_IMAGE_LUMIERE_SYMPA;
    // Le seuil profond REMPLACE le seuil chaos, il ne s'y multiplie pas.
    if (image <= SEUIL_IMAGE_CHAOS_PROFOND) {
        return dur ? MOD_IMAGE_CHAOS_PROFOND_DUR : MOD_IMAGE_CHAOS_PROFOND_SYMPA;
    }
    if (image <= SEUIL_IMAGE_CHAOS)
        return dur ? MOD_IMAGE_CHAOS_DUR : MOD_IMAGE_CHAOS_SYMPA;
    return 1;
}
/**
 * ⟦§7 · A-4 §2⟧ Le trait genré, **à sa place** : la fréquence, jamais le prix unitaire.
 * `genre: null` ⇒ aucun modificateur, comme partout ailleurs.
 */
export function modGenre(tonalite, genre) {
    return genre === "f" && TONALITES_DURES.includes(tonalite) ? MULT_TOXIQUES_F : 1;
}
/** ⟦§3.3⟧ Le poids effectif d'un candidat : son poids de contenu, coloré par l'état. */
export function poidsCommentaire(c, state) {
    const star = c.tonalite === "star" && hype(state) >= SEUIL_HYPE_STAR ? MOD_HYPE_STAR : 1;
    return (Math.max(0, c.poids) *
        modImage(c.tonalite, state.channel_image) *
        modGenre(c.tonalite, state.genre) *
        star);
}
/** Liste vide partagée : aucune allocation sur le chemin éteint, qui est le chemin normal. */
const AUCUN = Object.freeze([]);
/**
 * ⟦§3.1-3.3⟧ **Faire arriver des commentaires.** Ne mute rien — rend ce qu'il faut ajouter.
 *
 * 🔑 **Éteint, la fonction sort AVANT le premier `draw`.** C'est délibéré et c'est la garantie
 * d'état neutre la plus forte qu'on puisse donner : ce n'est pas « le Fil ne fait rien », c'est
 * « le Fil n'existe pas dans le flux d'aléa ». Une garde posée après un tirage aurait décalé
 * toutes les parties de tout le jeu.
 *
 * **Un seul `draw` par commentaire** (SPEC-MOTEUR §3.1), `index = rang dans l'arrivée (0..4)` :
 * le *nombre* de commentaires ne se tire pas, il se lit (§3.2). Tirage **sans remise**, et les
 * commentaires **déjà en file** sont exclus des candidats ⟦§3.3⟧ — un même `comment_id` ne peut
 * pas être présent deux fois dans le Fil au même moment.
 */
export function arriveeCommentaires(state, content, seed, ctx) {
    const uid0 = state.counters.fil_uid_next ?? 1;
    if (!filActif(content))
        return { ajoutes: AUCUN, uid_suivant: uid0 };
    const combien = nbCommentairesALArrivee(state, ctx);
    if (combien <= 0)
        return { ajoutes: AUCUN, uid_suivant: uid0 };
    const dejaEnFil = new Set(fil(state).map((c) => c.comment_id));
    const restants = vivierCommentaires(content).filter((c) => !dejaEnFil.has(c.id) && commentaireEligible(c, state, ctx));
    if (restants.length === 0)
        return { ajoutes: AUCUN, uid_suivant: uid0 };
    const ajoutes = [];
    let uid = uid0;
    for (let i = 0; i < combien && restants.length > 0; i++) {
        const poids = restants.map((c) => poidsCommentaire(c, state));
        const choisi = pickWeighted(poids, draw(seed, state.turn, "fil_draw", i));
        if (choisi < 0)
            break; // tous les poids sont nuls : personne ne parle
        const c = restants.splice(choisi, 1)[0];
        ajoutes.push({
            uid: uid++,
            comment_id: c.id,
            tonalite: c.tonalite,
            cadeau: (c.cadeau ?? null),
            pnj_id: c.pnj ? pnjDeLAuteur(state, content, c.auteur) : null,
            arrived_at_h: state.clock_h,
            expires_h: state.clock_h + FIL_PEREMPTION_H,
            epingle: false,
        });
    }
    return { ajoutes, uid_suivant: uid };
}
/**
 * ⟦§1.2 · A-4 D⟧ Le PNJ **de cette run** dont le nom est celui de l'auteur, ou `null`.
 *
 * Volontairement **sans tirage** : le lien passe par le nom écrit dans `content/comments.json`,
 * pas par un hasard. Un commentaire `star` dont le créateur n'a pas été tiré au bootstrap sort
 * avec `pnj_id: null` — il perd sa récurrence, et c'est l'appauvrissement que SPEC-FIL §13 Q7
 * demande d'assumer sciemment plutôt que de le masquer par un PNJ pris au hasard.
 */
function pnjDeLAuteur(state, content, auteur) {
    const presents = new Set((state.rivaux ?? []).map((r) => r.id));
    const trouve = (content.rivaux ?? []).find((r) => r.nom === auteur && presents.has(r.id));
    return trouve?.id ?? null;
}
/**
 * ⟦§3.4⟧ **Retirer ce qui a péri, puis borner la taille.** Ne mute rien.
 *
 * Deux règles dans cet ordre, et l'ordre compte : un commentaire périmé part **même si le Fil
 * n'est pas plein**, et le plafond ne se mesure qu'ensuite. L'inverse garderait des commentaires
 * morts au détriment de vivants.
 *
 * Les sortants ne produisent **aucun malus** — ils produisent une ligne narrative de
 * récapitulatif ⟦A-16, §9.4⟧. Le Fil ne peut jamais être pire que son absence (§0, propriété 2).
 *
 * ⚠️ **Le commentaire épinglé ne périt pas et ne sort pas au débordement** ⟦§4.4⟧ : l'épingler
 * est une décision du joueur, et la voir se défaire toute seule serait la défaire à sa place.
 */
export function vieillirFil(file, clockH) {
    const sortis = [];
    const vivants = file.filter((c) => {
        if (c.epingle || c.expires_h > clockH)
            return true;
        sortis.push(c);
        return false;
    });
    const borne = bornerFil(vivants);
    return { restants: borne.restants, sortis: [...sortis, ...borne.sortis] };
}
/**
 * ⟦§3.4⟧ **Borner la taille du Fil** : au-delà de `FIL_TAILLE_MAX`, les plus anciens sortent
 * d'abord, l'épinglé jamais. Ne mute rien. L'ordre d'origine est conservé — le Fil est ordonné
 * du plus ancien au plus récent ⟦§1.2⟧, et une UI qui s'appuierait sur cet ordre ne doit pas le
 * voir se réarranger sous elle.
 *
 * ⟦A-82⟧ Appelé à **deux** moments, et c'est la correction : la nuit (`vieillirFil`, après la
 * péremption) **et l'arrivée** (`arriveeFil`). La spec dit « si l'arrivée déborde, les plus
 * anciens sortent d'abord » — or le plafond n'était appliqué qu'à la nuit, donc après une
 * publication la file montait à 17 et n'était retaillée qu'au sommeil suivant. Le plafond est
 * aussi une borne d'état ⟦A38⟧ : il doit tenir à chaque écriture, pas à la prochaine.
 */
export function bornerFil(file) {
    if (file.length <= FIL_TAILLE_MAX)
        return { restants: file, sortis: [] };
    const sortis = [];
    let aRetirer = file.length - FIL_TAILLE_MAX;
    const restants = file.filter((c) => {
        if (aRetirer > 0 && !c.epingle) {
            aRetirer -= 1;
            sortis.push(c);
            return false;
        }
        return true;
    });
    return { restants, sortis };
}
// ─────────────────────────────────────────────────────────────────────────────
// §2.2 — Les gains de Hype
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§2.2⟧ **Ajouter (ou retirer) de la Hype.** MUTE `state` ; rend le delta **réellement
 * appliqué**, qui est ce que le récapitulatif ⟦A-16⟧ doit afficher — jamais le delta demandé.
 *
 * Deux raisons de passer par ici plutôt que d'écrire `state.hype += x` :
 *
 *  1. le clamp `[0 ; 100]` est appliqué une fois, au même endroit, pour toutes les sources ;
 *  2. **Fil éteint ⇒ aucun effet.** Sans cette garde, un `gauge_delta` de contenu pourrait
 *     remplir une jauge que personne ne lit, et le jour où on allume le Fil, des parties en
 *     cours démarreraient avec une Hype qu'elles n'ont jamais gagnée. L'interrupteur doit
 *     couper l'écriture, pas seulement la lecture ⟦§10⟧.
 */
export function gagnerHype(state, delta, content) {
    if (!filActif(content) || delta === 0)
        return 0;
    const avant = hype(state);
    const apres = clampHype(avant + delta);
    state.hype = apres;
    return apres - avant;
}
/**
 * ⟦§2.2⟧ Le gain de Hype d'une **publication**, en une lecture : le simple fait de sortir, plus
 * la prime du jet viral, plus celle d'un jalon réussi. Pure — le total est ensuite passé à
 * `gagnerHype` par le pas de publication (M3.21).
 *
 * Les trois se cumulent : une vidéo jalon virale est un très bon jour, et le jeu doit le dire.
 */
export function gainHypePublication(options = {}) {
    return (HYPE_PUBLICATION +
        (options.viral === true ? HYPE_VIRAL : 0) +
        (options.jalonReussi === true ? HYPE_JALON : 0));
}
// ─────────────────────────────────────────────────────────────────────────────
// §2.3 — L'effet de la Hype, et son SEUL effet
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§2.3 A⟧ **Le bonus de Portée de la Hype** : `0,05 × (hype / 100)`, à passer en `bonus` à
 * `portee()` (`formula.ts`). Rend **exactement 0** quand la Hype vaut 0 ou que le Fil est
 * éteint.
 *
 * ── Pourquoi `formula.ts` n'est PAS modifié, et c'est le point important ──────
 *
 * `portee(fidelite, momentum, bonus)` sait déjà ajouter un bonus **après** son clamp et borner
 * le total à `PLAFOND_PORTEE_TOTAL` (0,40). Ce n'est pas une coïncidence : ⟦A-17⟧ a ouvert
 * cette marge **pour la Hype**, en la nommant, et l'a livrée avec sa valeur neutre
 * (`bonus = 0` ⇒ le chiffre d'avant, au point près). Le travail de câblage était déjà fait ; le
 * refaire aurait touché le cœur calibré dont dépendent les six cibles de CI ⟦A-40⟧ pour ajouter
 * zéro comportement.
 *
 * ⚠️ **Conflit signalé avec PLAN §4.4** (règle CLAUDE.md), déjà tranché par ⟦A-17⟧ et rappelé
 * ici : PLAN borne la Portée à 0,35, et cette borne est **déjà saturée** par Fidélité 100 +
 * Momentum 1,5. Ajouter la Hype *avant* le clamp la rendrait nulle pour tout joueur bien géré —
 * la mécanique n'existerait que pour ceux qui n'en ont pas besoin. Elle s'ajoute donc **après**,
 * dans la marge de 0,05 qu'A-17 a ouverte. C'est la question n° 1 de SPEC-FIL §13, et le défaut
 * retenu est (a).
 *
 * 🔑 À `hype = 0` **et** Fil éteint, la valeur est `0` — pas « proche de 0 » : la même primitive
 * `portee()` avec le même argument par défaut, donc le même flottant, bit pour bit. C'est ce que
 * vérifie T-FIL-4, et c'est ce qui garantit qu'aucune ancre §12 ne bouge ⟦A-10⟧.
 */
export function bonusPorteeHype(state, content) {
    if (!filActif(content))
        return 0;
    const h = hype(state);
    return h <= 0 ? 0 : BONUS_PORTEE_HYPE_MAX * (h / 100);
}
// ─────────────────────────────────────────────────────────────────────────────
// §2.3 B — La vague de partage
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦§2.3 B⟧ La communauté va-t-elle **partager** cette publication ? Vrai au-delà de 70 de Hype,
 * Fil allumé. C'est une lecture — la décision de dépenser, elle, est `consommerVague`.
 */
export function vagueDePartage(state, content) {
    return filActif(content) && hype(state) >= SEUIL_VAGUE;
}
/**
 * ⟦§2.3 B⟧ Le facteur à figer dans `VideoRecord.boost_premiere_tranche` à la publication, ou
 * `undefined` s'il n'y a pas de vague.
 *
 * **`undefined` plutôt que `1`** : c'est l'état neutre littéral ⟦A-10⟧ — une vidéo publiée sans
 * vague, ou publiée avant le Fil, porte exactement la courbe d'aujourd'hui, et un état déjà
 * sérialisé reste valide sans migration.
 */
export function boostPremiereTranche(state, content) {
    return vagueDePartage(state, content) ? MULT_VAGUE : undefined;
}
/** Le facteur d'une vidéo déjà publiée. **Absent ⇔ 1,0** — jamais lu en direct ailleurs. */
export function boostDeLaVideo(video) {
    return video.boost_premiere_tranche ?? 1;
}
/**
 * ⟦§2.3 B⟧ **Appliquer la vague à une courbe de rendement.** Pure — rend une nouvelle courbe.
 *
 * ⚠️ **La PREMIÈRE tranche seulement**, et c'est tout le sens de la mécanique : PLAN §4.14 dit
 * « +20 % de vues sur les premières 24 h simulées », pas « +20 % de vues ». Multiplier la courbe
 * entière donnerait un bonus de rente permanent qui n'a rien à voir avec un partage — un partage
 * pousse le jour de la sortie, puis retombe.
 *
 * Le facteur s'applique **à la construction** de `remaining_curve` (publication, M3.21) : il est
 * figé là, comme `chance` et `taux_conversion`. Une Hype qui redescend ensuite ne reprend pas
 * les vues déjà promises.
 *
 * `boost` absent ou 1 ⇒ la **même** courbe, valeur par valeur : l'état neutre.
 */
export function appliquerVague(courbe, boost) {
    const facteur = boost ?? 1;
    if (facteur === 1 || courbe.length === 0)
        return [...courbe];
    return courbe.map((v, i) => (i === 0 ? v * facteur : v));
}
/**
 * ⟦§2.3 B⟧ **Dépenser la vague.** MUTE `state` ; rend le delta appliqué (≤ 0), pour la ligne
 * `fil · hype · −15 · « ta communauté a partagé »` du récapitulatif ⟦A-16 · §9.4⟧.
 *
 * À appeler **seulement si** `vagueDePartage` était vrai au moment de la publication : la
 * dépense et le bonus sont la même décision, et les séparer permettrait de payer sans recevoir.
 */
export function consommerVague(state, content) {
    if (!vagueDePartage(state, content))
        return 0;
    return gagnerHype(state, -HYPE_COUT_VAGUE, content);
}
/** Tolérance de flottant du report fractionnaire — voir la note dans `decroissanceHype`. */
const EPS_REPORT = 1e-9;
/** Une nuit qui n'a rien coûté. */
const AUCUNE_DECROISSANCE = Object.freeze({ delta: 0, reporte: 0 });
/**
 * ⟦§2.4⟧ **La décroissance de nuit.** MUTE `state` (`hype` et `counters.hype_frac`).
 *
 * ```
 * decay = HYPE_DECAY_JOUR × (Δh / H_JOUR)
 * × 2   si le silence dépasse 3 jours simulés   ⟦A-15 §5⟧
 * ÷ 2   si le nœud « Figure publique » est possédé
 * = 0   si une pause annoncée est en cours ⟦A-2⟧ — le gel couvre la Hype
 * ```
 *
 * **Le report fractionnaire n'est pas un détail comptable.** Une nuit fait 8 h, soit un tiers
 * de jour, soit 0,67 point : sans accumulateur, chaque nuit arrondirait à zéro et la cinquième
 * pression d'⟦A-15⟧ n'existerait tout simplement pas. On accumule dans `counters.hype_frac` et
 * on ne retire que des points **entiers** — la jauge bouge par pas visibles, et rien ne se perd.
 *
 * ⚠️ **`state.pause_announced` est lu en direct**, sans passer par `enPauseAnnoncee`
 * (`sleep.ts`) : c'est `sleep.ts` qui appellera ce module en M3.21, et un import dans l'autre
 * sens créerait le cycle à ce moment-là. Une lecture d'un champ, contre un cycle : le marché
 * est vite fait, et la règle tient en une ligne juste au-dessus.
 *
 * @param deltaH heures de calendrier écoulées sur ce pas.
 * @param silenceH heures de silence, **gel de pause déduit** (`heuresDeSilence`, `sleep.ts`).
 *   Absent ⇒ dérivé de `counters.last_publication_h` ; jamais publié ⇒ silence infini (§2.4).
 */
export function decroissanceHype(state, deltaH, content, silenceH) {
    if (!filActif(content) || deltaH <= 0)
        return AUCUNE_DECROISSANCE;
    if (state.pause_announced != null)
        return AUCUNE_DECROISSANCE; // ⟦A-2⟧ le gel couvre la Hype
    if (hype(state) <= 0 && (state.counters.hype_frac ?? 0) <= 0)
        return AUCUNE_DECROISSANCE;
    let decay = HYPE_DECAY_JOUR * (deltaH / H_JOUR);
    if (silenceEffectif(state, silenceH) > H_HYPE_TOLERANCE_SILENCE)
        decay *= MULT_DECAY_SILENCE;
    if (state.skills.includes(NOEUD_FIGURE_PUBLIQUE))
        decay /= DIV_DECAY_FIGURE_PUBLIQUE;
    // ⚠️ `+ EPS_REPORT` avant le plancher, et ce n'est pas de la coquetterie : trois nuits de 8 h
    // valent exactement un jour, mais `3 × (2 × 8/24)` vaut 1,9999999999999998 en flottant. Sans
    // l'epsilon, la troisième nuit ne retirerait rien et **un point disparaîtrait tous les trois
    // jours** — précisément la perte que le report fractionnaire existe pour empêcher, et elle
    // serait invisible.
    const cumul = (state.counters.hype_frac ?? 0) + decay;
    const entiers = Math.floor(cumul + EPS_REPORT);
    const reporte = Math.max(0, cumul - entiers);
    state.counters.hype_frac = reporte;
    if (entiers <= 0)
        return { delta: 0, reporte };
    const avant = hype(state);
    const apres = Math.max(0, avant - entiers);
    state.hype = apres;
    return { delta: apres - avant, reporte };
}
/**
 * Heures de silence à retenir pour la décroissance. **Jamais publié ⇒ infini** (§2.4) — ce qui
 * est sans conséquence au démarrage, où la Hype vaut 0 et où `decroissanceHype` sort avant.
 */
function silenceEffectif(state, silenceH) {
    if (silenceH !== undefined)
        return Math.max(0, silenceH);
    const derniere = state.counters.last_publication_h;
    if (derniere === null || derniere === undefined)
        return Number.POSITIVE_INFINITY;
    return Math.max(0, state.clock_h - derniere);
}
const AUCUNE_ARRIVEE = Object.freeze({
    ajoutes: AUCUN,
    decouverte: false,
    sortis: AUCUN,
    harcelement_pose: false,
});
/**
 * ⟦A-102 §3⟧ **Nombre de `toxique` reçus avant que la vague narrative ne se pose.** ✎
 *
 * Choisi pour tomber ni trop tôt (l'arc 2 vient juste de découvrir le Fil, §3.2) ni jamais
 * (une run courte ou peu commentée ne verrait rien) — mesuré avant l'allumage, voir l'avenant
 * pour le chiffre observé sur la campagne allumée.
 */
export const FIL_SEUIL_HARCELEMENT = 20;
/**
 * ⟦A-102 §3⟧ L'id de contenu que `majHarcelement` programme (`programmerEvenement`) — DOIT
 * exister dans `content/events.json`, `tirable: false` (vérifié par un test dédié, comme
 * `EVENEMENT_PASSATION` l'est dans `events.ts`).
 */
export const EVENEMENT_VAGUE_HARCELEMENT = "fil_vague_harcelement";
/**
 * ⟦A-102 §3⟧ **« Le harcèlement se raconte, il ne se simule pas. »** Compte les `toxique`
 * reçus (arrivés dans le Fil, lus ou non — recevoir une vague, c'est déjà ça, avant même d'y
 * répondre) et **programme une fois par run** l'événement `fil_vague_harcelement`
 * (`programmerEvenement`, §5.2) quand le compte franchit `FIL_SEUIL_HARCELEMENT`.
 *
 * ⚠️ **Pas un `flags_requis`, cette fois — un `event_schedule` hors roulette**, exactement
 * comme la passation du n°1 de MeTube (`events.ts`, `EVENEMENT_PASSATION`) ou le hook
 * `star` de ce même fichier (§5.2). Deux raisons :
 *  1. Un choix de contenu ne peut PAS retirer un flag posé par le moteur (SPEC-CONTENU T13,
 *     « le contenu ne peut que lire un flag moteur ») — un `flags_requis` aurait donc laissé
 *     l'événement éligible pour toujours après la première pose, avec aucun moyen propre de
 *     le refermer depuis `content/events.json`.
 *  2. `tirable: false` est *plus sûr* que « jamais `tirable: true` sec » : l'événement n'entre
 *     JAMAIS dans le pool pondéré, pour personne, quel que soit `fil_actif` — seul
 *     `programmerEvenement` peut l'armer, et lui seul tourne quand le Fil est allumé.
 *
 * `flags.fil_harcelement_vecu` (permanent, jamais lu par le contenu) garde la règle « une
 * seule vague par run » : sans lui, un lot de toxiques suivant continuerait à repousser
 * `programmerEvenement`, dupliquant l'entrée programmée (`ScheduledEvent[]` n'est pas dédupé
 * par id, voir le hook `star` du même fichier, gardé par une variable locale plutôt que par
 * l'état — ici la garde doit survivre bien plus d'un appel, donc elle vit dans `state.flags`).
 *
 * Aucun `draw` : c'est un compte sur un résultat déjà tiré (`arriveeCommentaires`, `fil_draw`),
 * pas un second jet — le vocabulaire RNG de SPEC-MOTEUR §3.2 reste fermé.
 */
export function majHarcelement(state, toxiquesArrives) {
    if (toxiquesArrives <= 0 || state.flags.fil_harcelement_vecu === true)
        return false;
    state.counters.fil_toxiques_recus = (state.counters.fil_toxiques_recus ?? 0) + toxiquesArrives;
    if (state.counters.fil_toxiques_recus < FIL_SEUIL_HARCELEMENT)
        return false;
    state.flags.fil_harcelement_vecu = true;
    programmerEvenement(state, EVENEMENT_VAGUE_HARCELEMENT);
    return true;
}
/**
 * ⟦§3.1⟧ **Faire arriver les commentaires, et les poser dans l'état.** MUTE `state`.
 *
 * Appelée au **pas 6bis** de la file de résolution (SPEC-MOTEUR §2.3) : après l'effet propre de
 * l'action, **avant** le tirage d'événement — pour qu'un cadeau `hook_evenement` puisse peser
 * sur le tirage du même tour ⟦§5.2⟧.
 *
 * 🔑 Éteinte, elle sort **avant** `arriveeCommentaires`, donc avant le premier `draw` : le Fil
 * n'existe pas dans le flux d'aléa, et une campagne reste identique au caractère près ⟦A-10⟧.
 */
export function arriveeFil(state, content, seed, moment, options = {}) {
    if (!filActif(content))
        return AUCUNE_ARRIVEE;
    const { ajoutes, uid_suivant } = arriveeCommentaires(state, content, seed, contexteArrivee(state, moment, options));
    if (ajoutes.length === 0)
        return AUCUNE_ARRIVEE;
    // ⟦§3.4 · A-82⟧ Le plafond tient À L'ARRIVÉE : si elle déborde, les plus anciens sortent
    //   d'abord. Les nouveaux venus sont en queue, donc jamais retirés par leur propre arrivée.
    const { restants, sortis } = bornerFil([...fil(state), ...ajoutes]);
    state.fil = [...restants];
    state.counters.fil_uid_next = uid_suivant;
    const decouverte = state.flags.fil_decouvert !== true;
    if (decouverte)
        state.flags.fil_decouvert = true;
    // ⟦A-102 §3⟧ Le harcèlement se COMPTE ici (l'arrivée est déjà tirée, aucun `draw` de plus) —
    // jamais un jet à part : le vocabulaire RNG de SPEC-MOTEUR §3.2 reste fermé.
    const toxiquesArrives = ajoutes.filter((c) => c.tonalite === "toxique").length;
    const harcelement_pose = majHarcelement(state, toxiquesArrives);
    return { ajoutes, decouverte, sortis, harcelement_pose };
}
/**
 * Le contexte d'arrivée, **lu sur l'état** plutôt que reconstruit par l'appelant : la classe et
 * le format de la vidéo qui vient de sortir sont dans `state.videos`, et un appelant qui les
 * recopierait finirait par les recopier faux.
 *
 * ⚠️ La vidéo n'est retenue que si elle a été publiée **à ce tour** : une vidéo jalon ne crée
 * aucun `VideoRecord` (`jalons.ts`), donc les conditions `classe_video` / `format_video` ne
 * doivent pas s'y accrocher à la dernière vidéo en date, qui n'a rien à voir.
 */
function contexteArrivee(state, moment, options) {
    if (moment !== "publication") {
        return { publication: false, silenceH: options.silenceH };
    }
    const derniere = state.videos[state.videos.length - 1];
    const deCeTour = derniere?.published_turn === state.turn ? derniere : undefined;
    return {
        publication: true,
        classe: deCeTour?.class,
        format: deCeTour?.format,
        virale: options.viral === true,
        silenceH: options.silenceH,
    };
}
const AUCUN_CM = Object.freeze({
    traites: Object.freeze([]),
    cout: 0,
    impaye: false,
});
const AUCUNE_NUIT_FIL = Object.freeze({
    hype: AUCUNE_DECROISSANCE,
    sortis: AUCUN,
    cm: AUCUN_CM,
    abandon: 0,
    censure_posee: false,
});
/**
 * ⟦§9.5 · A-82⟧ **Le community manager est-il en poste ?** Quatre conditions, toutes lues :
 * le Fil allumé, le Grand Saut fait (avant, l'argent n'existe pas ⟦A-1 · A-29⟧), la capacité
 * débloquée par l'arbre, et le joueur qui ne l'a pas arrêté. **Absent ⇒ en poste dès que le
 * nœud est acheté** — acheter, c'est embaucher, la même convention que le producteur et le
 * manager ⟦SPEC-PRODUCTEUR §6.3⟧ ; l'action `regler_cm` est la porte de sortie, et de retour.
 */
export function cmEnPoste(state, content) {
    if (!filActif(content))
        return false;
    if (state.flags.grand_saut_done !== true)
        return false;
    if (state.flags.cm_actif === false)
        return false;
    return capaciteDeLArbre(state, content, CAPACITE_COMMUNITY_MANAGER);
}
/** ⟦§9.5⟧ Le salaire d'un pas de `deltaH` heures — pro rata de la semaine, au centime. */
export function coutCM(deltaH) {
    return Math.round(((CM_COUT_HEBDO * deltaH) / H_SEMAINE) * 100) / 100;
}
/**
 * ⟦§9.5⟧ Ce que le CM traiterait **maintenant** : les `CM_TRAITEMENT` plus anciens commentaires
 * de routine (`TONALITES_ROUTINE`, sans cadeau, non épinglés, et dont le contenu autorise
 * `coeur`). Lecture pure — l'écran peut la montrer avant la nuit ⟦A-16⟧.
 */
export function candidatsCM(state, content) {
    return fil(state)
        .filter((c) => !c.epingle &&
        c.cadeau === null &&
        TONALITES_ROUTINE.includes(c.tonalite) &&
        (contenuDuCommentaire(content, c.comment_id)?.reponses.includes("coeur") ?? false))
        .slice(0, CM_TRAITEMENT);
}
/**
 * ⟦§9.5 · A-82⟧ **La nuit du community manager.** MUTE `state`. **Aucun `draw`** : il ne fait
 * que des `coeur`, la seule réponse sans jet — un salarié qui tenterait des vannes à ta place
 * consommerait ton aléa et, pire, déciderait pour toi.
 *
 * Il est payé pour la nuit, travail ou pas (un salaire, pas une commission) ; si la caisse ne
 * couvre pas la nuit, il ne fait rien et rien n'est dû — pas d'escalier d'impayés : c'est au
 * joueur de l'arrêter, et le journal le lui dit. Ses traitements **ne remettent pas à zéro** le
 * compteur d'abandon ⟦§4.5⟧ : c'est le joueur qui revient, pas son employé — mais une file qu'il
 * vide passe sous le seuil de 8, et c'est bien là son confort.
 */
export function nuitCM(state, content, deltaH) {
    if (deltaH <= 0 || !cmEnPoste(state, content))
        return AUCUN_CM;
    const cout = coutCM(deltaH);
    if (state.money < cout)
        return { traites: AUCUN_CM.traites, cout: 0, impaye: true };
    state.money -= cout;
    const traites = [];
    for (const c of candidatsCM(state, content)) {
        const effet = {
            uid: c.uid,
            comment_id: c.comment_id,
            tonalite: c.tonalite,
            reponse: "coeur",
            fidelite: 0,
            hype: 0,
            image: 0,
            sm: 0,
            humour: null,
            cadeau: null,
            cadeau_perdu: null,
        };
        // ⟦§4.2⟧ Un `coeur` à effet ÷2 — sans *Cœur sur vous* : la phrase est la tienne, pas la
        // sienne. Le gain de Fidélité passe par le trait ⟦A-4 §2⟧ comme tout gain de jauge.
        const fid = appliquerTraitJauge(state.genre, "loyalty", FID_COEUR / CM_DIV_EFFET);
        state.loyalty += fid;
        effet.fidelite += fid;
        effet.hype += gagnerHype(state, HYPE_COEUR / CM_DIV_EFFET, content);
        state.fil = fil(state).filter((x) => x.uid !== c.uid);
        traites.push(effet);
    }
    return { traites, cout, impaye: false };
}
/**
 * ⟦§2.4 · §3.4⟧ **Le pas de nuit du Fil.** MUTE `state`.
 *
 * Deux choses, dans cet ordre : la Hype redescend (la cinquième pression d'⟦A-15⟧ — le monde
 * continue sans toi), puis la file vieillit et ce qui a péri en sort. L'ordre est sans
 * conséquence mécanique (la décroissance ne lit pas la file) mais il est fixé pour rester
 * déterministe si un jour l'une des deux venait à lire l'autre.
 *
 * **Aucun `draw`** : ni la décroissance ni le vieillissement ne tirent. Le seul aléa du Fil est
 * l'arrivée (`fil_draw`), et elle se joue au pas 6bis, pas ici.
 *
 * @param silenceH heures de silence **gel de pause déduit** (`heuresDeSilence`, `sleep.ts`).
 */
export function nuitFil(state, content, deltaH, silenceH) {
    if (!filActif(content))
        return AUCUNE_NUIT_FIL;
    const hypeDelta = decroissanceHype(state, deltaH, content, silenceH);
    const avant = fil(state);
    const { restants, sortis } = vieillirFil(avant, state.clock_h);
    if (restants.length !== avant.length)
        state.fil = [...restants];
    // ⟦§9.5 · A-82⟧ Le community manager passe APRÈS le vieillissement (il ne répond pas à ce
    //   qui est déjà parti) et AVANT le malus d'abandon (la file qu'il a allégée est celle que
    //   le joueur verra au réveil — et celle sur laquelle le malus se mesure).
    const cm = nuitCM(state, content, deltaH);
    // ⟦§4.5⟧ Le malus d'abandon se mesure ICI et pas sur la réponse `ignorer` : un joueur qui
    //   trie ses commentaires n'est pas un joueur qui abandonne sa communauté. Mesuré après le
    //   vieillissement, sur la file telle qu'elle est au réveil — celle que le joueur verra.
    const abandon = malusAbandon(state);
    // ⟦§4.6⟧ La fenêtre d'abus glisse avec l'horloge : les suppressions trop vieilles sortent du
    //   compteur, et `fil_censure` se **dépose tout seul** quand elles sont sorties. Un flag qui
    //   ne sait pas expirer serait une condamnation à vie pour cinq clics.
    const censurePosee = majCensure(state);
    return { hype: hypeDelta, sortis, cm, abandon, censure_posee: censurePosee };
}
/**
 * ⟦§4.5⟧ **Le malus d'abandon.** MUTE `state` ; rend le delta de Fidélité appliqué (≤ 0).
 *
 * ```
 * à la nuit :
 *   si |fil| ≥ 8 : fil_nuits_sans_lecture += 1
 *                  si fil_nuits_sans_lecture > 3 : Fidélité −0,5   // par nuit, jamais en pente
 *   sinon        : fil_nuits_sans_lecture = 0
 * ```
 *
 * 🔑 **Où est passée la troisième condition (« aucun commentaire traité depuis la nuit
 * précédente ») ?** Elle est portée par le compteur lui-même : `traiterFil` le **remet à zéro**
 * dès qu'un commentaire est traité. Un joueur qui a répondu à un seul message repart donc de 0
 * et ne peut plus atteindre 4 avant quatre nouvelles nuits de silence. Écrire la condition une
 * seconde fois ici aurait demandé un instantané du compteur de traitements à chaque nuit —
 * c'est-à-dire une deuxième mémoire de la même vérité, et un jour de divergence garanti.
 *
 * Trois conditions **cumulées** : impossible à déclencher par accident, visible d'un coup d'œil
 * (le Fil déborde), et annulable en traitant **un seul** commentaire. C'est la lecture d'⟦A-15⟧
 * *« prévenir, jamais surprendre »*.
 *
 * ⚠️ **Aucun trait de genre** : c'est une PERTE de Fidélité, et `traits.ts` règle 1 est
 * explicite — les traits ne pèsent que sur les gains.
 */
export function malusAbandon(state) {
    if (fil(state).length < FIL_SEUIL_ABANDON) {
        state.counters.fil_nuits_sans_lecture = 0;
        return 0;
    }
    const nuits = (state.counters.fil_nuits_sans_lecture ?? 0) + 1;
    state.counters.fil_nuits_sans_lecture = nuits;
    if (nuits <= FIL_NUITS_TOLEREES)
        return 0;
    state.loyalty -= FIL_MALUS_ABANDON;
    return -FIL_MALUS_ABANDON;
}
/**
 * ⟦§4.6⟧ **L'abus de censure.** MUTE `state` (purge la fenêtre, pose ou retire `fil_censure`).
 * Rend `true` si le drapeau vient de se poser.
 *
 * Le flag **ne fait rien par lui-même** : il rend éligible un événement de contenu `bad_buzz`
 * « censure » (`flags_requis: ["fil_censure"]`). On réutilise le système d'événements plutôt
 * que d'écrire un bad buzz en dur — règle CLAUDE.md, et c'est aussi ce qui permet à Mathieu
 * d'en écrire les mots sans toucher au moteur.
 */
export function majCensure(state) {
    const fenetre = (state.counters.fil_suppressions ?? []).filter((h) => h > state.clock_h - H_FIL_FENETRE_ABUS);
    if (fenetre.length === 0) {
        if (state.counters.fil_suppressions !== undefined)
            state.counters.fil_suppressions = [];
        if (state.flags.fil_censure === true)
            state.flags.fil_censure = false;
        return false;
    }
    state.counters.fil_suppressions = fenetre;
    const abus = poidsSuppressions(state) > FIL_SUPPRESSIONS_TOLEREES;
    const avant = state.flags.fil_censure === true;
    state.flags.fil_censure = abus;
    return abus && !avant;
}
/**
 * ⟦§4.6⟧ Le poids des suppressions **dans la fenêtre**. Une suppression de `troll` ou de
 * `toxique` compte 1 ; toute autre compte **double** — supprimer une critique légitime, c'est
 * ça, la censure, et c'est la seule chose que le compteur cherche à voir.
 *
 * Le poids est stocké **au moment de la suppression** : chaque entrée de `fil_suppressions`
 * est un `clock_h` répété autant de fois qu'elle pèse. Une seule forme de donnée, aucune
 * seconde liste à garder en phase, et un état sérialisé avant ce commit reste lisible.
 */
function poidsSuppressions(state) {
    return (state.counters.fil_suppressions ?? []).length;
}
const AUCUNE_PUBLICATION_FIL = Object.freeze({
    hype_gagnee: 0,
    vague: false,
    hype_vague: 0,
});
/**
 * ⟦§2.2 · §2.3 B⟧ **Le pas de publication du Fil.** MUTE `state` et, s'il y a vague, la vidéo.
 *
 * ⚠️ **L'ordre des deux mouvements de Hype n'est pas commutatif, et il est délibéré** : la
 * vague se décide sur la Hype d'**avant** la publication — c'est la communauté telle qu'elle
 * était quand la vidéo est sortie qui la partage —, puis le gain de la publication s'ajoute.
 * L'ordre inverse ferait franchir le seuil de 70 à une vidéo par son propre gain, ce qui
 * reviendrait à se faire partager par une communauté qui n'a pas encore vu la vidéo.
 *
 * `video` vaut `null` pour une **vidéo jalon** : elle ne crée aucun `VideoRecord`, donc aucune
 * courbe à booster — mais elle vaut bien ses points de Hype (`jalonReussi`).
 */
export function publicationFil(state, content, video, options = {}) {
    if (!filActif(content))
        return AUCUNE_PUBLICATION_FIL;
    let hypeVague = 0;
    const boost = video === null ? undefined : boostPremiereTranche(state, content);
    if (boost !== undefined && video !== null) {
        video.boost_premiere_tranche = boost;
        video.remaining_curve = appliquerVague(video.remaining_curve, boost);
        hypeVague = consommerVague(state, content);
    }
    const gagnee = gagnerHype(state, gainHypePublication(options), content);
    return { hype_gagnee: gagnee, vague: boost !== undefined, hype_vague: hypeVague };
}
// ─────────────────────────────────────────────────────────────────────────────
// §4-§5 — LES CINQ RÉPONSES ET LES TROIS CADEAUX
// ─────────────────────────────────────────────────────────────────────────────
//
// C'est le maillon que le joueur TOUCHE. Tout ce qui précède fait arriver des commentaires et
// les fait vieillir ; à partir d'ici, il en fait quelque chose.
//
// ── Le partage du travail avec `actions.ts`, et pourquoi il est là ────────────
//
// Le **coût de santé mentale** des commentaires lus est calculé par `coutSMTraitements` et
// facturé par la table §2.2 (`actions.ts`), **pas** ici. Ce n'est pas un caprice
// d'architecture : ⟦A-11 §1⟧ exige que le coût prévisualisé soit exactement celui qui sera
// débité — le client affiche `actionCost` avant le clic. Un coût de SM appliqué au fond de la
// résolution ferait mentir cet affichage, et le joueur découvrirait la facture après coup.
//
// ⟦A-113 §2⟧ Ce coût ne dépend plus DU TOUT de la réponse choisie : lire un commentaire coûte
// sa tonalité, point — `coeur`, `humour`, `epingler`, `ignorer`, `supprimer` payent pareil.
//
// Ce qui reste ici, c'est ce qu'aucune prévisualisation ne peut promettre : le **résultat** —
// Fidélité, Hype, image, cadeaux, et le jet d'humour qui peut mal tourner. Le SM d'une vanne
// ratée fait partie du résultat, pas du coût : on ne peut pas afficher « −2 SM » avant de
// savoir si la vanne passe.
/**
 * ⟦§4 · §9.5⟧ Combien de commentaires le joueur peut traiter en une action.
 *
 * Trois par défaut — la boucle du Fil doit rester « 20-30 secondes par session » (PLAN §4.14) —
 * et **quatre** avec le nœud 💬 *Réponses aux commentaires* : traiter plus vite, pas mieux.
 */
export function filLot(state) {
    return state.skills.includes(NOEUD_REPONSES_COMMENTAIRES) ? FIL_LOT + 1 : FIL_LOT;
}
/** Le commentaire de la file qui porte cet `uid`, ou `undefined`. */
export function commentaireEnFil(state, uid) {
    return fil(state).find((c) => c.uid === uid);
}
/** L'entrée de contenu d'un commentaire en file — c'est elle qui porte le texte et les réponses. */
export function contenuDuCommentaire(content, commentId) {
    return vivierCommentaires(content).find((c) => c.id === commentId);
}
// ── §4.1 Le coût de santé mentale, seul morceau qui vit dans la table des coûts ──
/**
 * ⟦§4.1 · A-113 §2⟧ Ce qu'un commentaire coûte en santé mentale, **selon sa seule tonalité** —
 * la réponse n'entre plus dans le calcul.
 *
 * ```
 * toxique → −1,5   ·   troll → −0,5   ·   autres → 0
 * × 2    si l'image est ≤ −40 (PLAN §4.7 « lire les commentaires coûte double »)
 * × 0    si le nœud 💬 Gestion de crise est possédé (« les toxiques ne coûtent plus de SM »)
 * ```
 *
 * 🔴 **Ce qui a disparu, et pourquoi.** Décision de Mathieu (25/08/2026) : *« On ne va pas
 * appuyer sur un bouton "lire". Tu es obligé de le lire pour le supprimer, pour répondre, pour
 * ne rien faire — pour tout. »* `coeur`, `humour`, `epingler`, `ignorer` et `supprimer` payent
 * désormais EXACTEMENT le même prix : la claque se paie à la lecture, jamais au geste qui
 * suit. Ce chiffre **REMPLACE le barème d'⟦A-102 §1 / A-111 §1⟧**, qui faisait payer
 * `coeur`/`epingler` le double sur un dur (§4.2/§4.4 d'origine, le « risque » de PLAN §4.14) et
 * `supprimer` la moitié (`RATIO_SM_SUPPRESSION`, retiré) — les réponses portent encore leurs
 * EFFETS (Hype, Fidélité, risque de censure, voir `traiterFil`), plus leur propre prix de SM.
 *
 * 🔑 **Le prix est le même pour les deux genres**, et c'est la propriété que verrouille
 * T-FIL-6 ⟦§7⟧ : le trait « le Fil est plus rude » pèse sur la **fréquence** des toxiques
 * (`MULT_TOXIQUES_F`, le tirage), jamais sur leur prix unitaire. Les deux ensemble feraient
 * payer le trait deux fois, et le déséquilibre serait invisible.
 */
export function coutSMCommentaire(state, tonalite) {
    if (state.skills.includes(NOEUD_GESTION_DE_CRISE_FIL))
        return 0;
    const base = tonalite === "toxique" ? SM_TOXIQUE : tonalite === "troll" ? SM_TROLL : 0;
    if (base === 0)
        return 0;
    return state.channel_image <= SEUIL_IMAGE_DOUBLE_PEINE ? base * 2 : base;
}
/**
 * ⟦§6.2⟧ Le `mental_delta` d'une action `lire_commentaires` **avec** charge utile : la somme
 * des coûts de tonalité des commentaires réellement traités.
 *
 * Le forfait de l'action ne s'applique **pas** en plus : on ne paie pas deux fois. C'est la
 * seule façon d'être honnête — un joueur qui tombe sur trois messages adorables ne perd rien,
 * un joueur qui encaisse trois messages haineux paie plus que le forfait. Les traitements
 * inconnus de la file sont ignorés ici : c'est `validerLotFil` qui les refuse, et une
 * prévisualisation ne doit jamais lever d'erreur.
 */
export function coutSMTraitements(state, traitements) {
    let total = 0;
    for (const t of traitements) {
        const c = commentaireEnFil(state, t.uid);
        if (c)
            total += coutSMCommentaire(state, c.tonalite);
    }
    return total;
}
// ── §1.4 La validation, avant tout effet et avant tout tirage ────────────────
/**
 * ⟦§1.4⟧ **Le lot est-il jouable ?** Lève `InvalidAction` sinon — donc **aucun effet, aucun
 * turn, aucun tirage** (SPEC-MOTEUR §2.3 pas 1). Ne mute rien.
 *
 * Quatre refus, dans cet ordre : lot trop grand, `uid` inconnu, `uid` répété, réponse que le
 * contenu n'autorise pas (ou seconde épingle sur le même commentaire).
 */
export function validerLotFil(state, content, traitements) {
    const lot = filLot(state);
    if (traitements.length > lot) {
        throw new InvalidAction("fil_lot_depasse", { lot, demandes: traitements.length });
    }
    const vus = new Set();
    for (const t of traitements) {
        if (vus.has(t.uid))
            throw new InvalidAction("fil_comment_unknown", { uid: t.uid, raison: "double" });
        vus.add(t.uid);
        const enFil = commentaireEnFil(state, t.uid);
        if (!enFil)
            throw new InvalidAction("fil_comment_unknown", { uid: t.uid, raison: "absent" });
        // Le contenu décide des réponses proposées ⟦§8.2⟧ — « un commentaire qu'on ne peut pas
        // supprimer (celui d'un gros créateur), ou qu'on ne peut qu'ignorer ». Un commentaire dont
        // l'entrée de contenu a disparu garde les cinq réponses : le moteur ne bloque pas le joueur
        // sur une régression de contenu, il la laisse se voir dans les tests de cohérence.
        const c = contenuDuCommentaire(content, enFil.comment_id);
        if (c && !c.reponses.includes(t.reponse)) {
            throw new InvalidAction("fil_reponse_interdite", {
                uid: t.uid,
                reponse: t.reponse,
                raison: "non_autorisee",
            });
        }
        if (t.reponse === "epingler" && enFil.epingle) {
            throw new InvalidAction("fil_reponse_interdite", {
                uid: t.uid,
                reponse: t.reponse,
                raison: "deja_epingle",
            });
        }
    }
}
// ── §4.3 Le jet d'humour ─────────────────────────────────────────────────────
/**
 * ⟦§4.3⟧ La probabilité qu'une vanne passe. **Lecture pure** — l'UI peut l'afficher avant le
 * clic ⟦A-16, « montrer le calcul »⟧, et c'est bien le but : `humour` est la seule réponse qui
 * se joue à pile ou face, le joueur a le droit de savoir à quelle pièce.
 *
 * ```
 * 0,55 + 0,05 × (nœuds 💬 Charisme possédés) − 0,10 si SM ≤ 29, borné à 0,90
 * ```
 *
 * Le malus de zone rouge n'est pas décoratif : on rate ses vannes quand on va mal, et ça rend
 * la santé mentale lisible dans un geste minuscule.
 */
export function probaHumour(state, content) {
    const p = P_HUMOUR_BASE +
        P_HUMOUR_PAR_NOEUD * noeudsCharisme(state, content) +
        (state.mental <= SM_ZONE_ROUGE ? P_HUMOUR_MALUS_ZONE_ROUGE : 0);
    return Math.max(0, Math.min(P_HUMOUR_MAX, p));
}
/**
 * Les nœuds 💬 Charisme **possédés**. La branche est lue dans le contenu, jamais recopiée ici :
 * une liste de nœuds en dur dans le moteur serait une règle de contenu écrite au mauvais
 * endroit (CLAUDE.md), et elle se périmerait au premier nœud ajouté à l'arbre.
 */
function noeudsCharisme(state, content) {
    const branches = new Map((content.skills ?? []).map((s) => [s.id, s.branche]));
    return state.skills.filter((id) => branches.get(id) === BRANCHE_CHARISME).length;
}
const AUCUN_LOT = Object.freeze({
    traites: Object.freeze([]),
    cartes: Object.freeze([]),
    hook: null,
    censure_posee: false,
});
/**
 * ⟦§4 · §5⟧ **Traiter un lot de commentaires.** MUTE `state`. Rend ce qui s'est passé.
 *
 * L'ordre du §4.1, pour chaque commentaire et **dans l'ordre du lot** :
 *
 * 1. le coût de santé mentale de la tonalité — **facturé par `actionCost`**, voir l'en-tête,
 *    et **identique quelle que soit la réponse** depuis ⟦A-113 §2⟧ ;
 * 2. l'effet propre de la réponse (§4.2 → §4.6) ;
 * 3. le bonus `star` : Hype +4 si la réponse n'est pas `ignorer` ;
 * 4. le cadeau éventuel (§5) — **jamais** sur `supprimer` ;
 * 5. le commentaire quitte le Fil, et `counters.fil_traites` avance.
 *
 * ⚠️ **`epingler` est la seule réponse qui laisse le commentaire dans la file** (§4.4) : c'est
 * ce qui rend l'épingle visible. Son effet ne s'applique qu'une fois — `validerLotFil` refuse
 * de l'épingler deux fois.
 *
 * 🔑 **Le compteur de nuits sans lecture repart de zéro ici**, et c'est la troisième condition
 * du malus d'abandon (§4.5) : traiter **un seul** commentaire suffit à annuler quatre nuits de
 * négligence. Le joueur qui revient n'a pas de dette à rattraper.
 *
 * Un seul `draw` par vanne (`fil_outcome`, index = rang dans le lot) : les quatre autres
 * réponses ne tirent rien, et un lot sans humour ne consomme aucun aléa.
 */
export function traiterFil(state, content, seed, traitements) {
    if (!filActif(content) || traitements.length === 0)
        return AUCUN_LOT;
    const traites = [];
    const cartes = [];
    let hook = null;
    let censurePosee = false;
    for (let rang = 0; rang < traitements.length; rang++) {
        const t = traitements[rang];
        const enFil = commentaireEnFil(state, t.uid);
        if (!enFil)
            continue; // `validerLotFil` l'a déjà refusé ; filet de sécurité, jamais atteint.
        const effet = {
            uid: enFil.uid,
            comment_id: enFil.comment_id,
            tonalite: enFil.tonalite,
            reponse: t.reponse,
            fidelite: 0,
            hype: 0,
            image: 0,
            sm: 0,
            humour: null,
            cadeau: enFil.cadeau,
            cadeau_perdu: null,
        };
        // — 2. l'effet propre de la réponse —
        switch (t.reponse) {
            case "coeur":
                appliquerCoeur(state, content, effet);
                break;
            case "humour":
                appliquerHumour(state, content, seed, rang, effet);
                break;
            case "epingler":
                appliquerEpingle(state, content, enFil, effet);
                break;
            case "ignorer":
                // Aucun effet DE GAUGE ici — mais pas « coût nul » : le coût de SM de la tonalité est
                // payé en amont par `coutSMTraitements`, au même prix qu'un `coeur` ou un `supprimer`
                // ⟦A-113 §2⟧. Ce que `ignorer` perd vraiment, c'est l'UPSIDE des autres réponses
                // (Fidélité/Hype de `coeur`, jet d'`humour`…) pour le même prix — voir l'audit ⟦A-113
                // §2.4⟧. Le malus d'abandon de PLAN §4.14 ne se mesure PAS ici non plus (§4.5).
                break;
            case "supprimer":
                censurePosee = appliquerSuppression(state, enFil) || censurePosee;
                break;
        }
        // — 3. le bonus de tonalité `star` : un gros créateur a commenté, et on lui a répondu —
        if (enFil.tonalite === "star" && t.reponse !== "ignorer") {
            effet.hype += gagnerHype(state, HYPE_STAR, content);
        }
        // — 4. le cadeau. Jamais sur `supprimer` : on ne reçoit rien de ce qu'on efface —
        if (enFil.cadeau !== null && t.reponse !== "supprimer") {
            if (enFil.cadeau.type === "idee") {
                cartes.push(INDEX_CADEAU_IDEE + rang);
            }
            else if (enFil.cadeau.type === "hook_evenement") {
                // ⟦§5.2⟧ **Un seul hook armé par tour.** Sans cette règle, le « un seul tirage
                // d'événement par avancée de temps » de PLAN §4.9 tomberait. Le second est perdu, et
                // il le dit — une ligne de récapitulatif plutôt qu'un silence.
                if (hook === null) {
                    hook = enFil.cadeau.event_id;
                    programmerEvenement(state, hook);
                }
                else {
                    effet.cadeau_perdu = "hook_deja_arme";
                }
            }
            // `verbatim` : aucun effet mécanique. Il consomme une place du lot, et c'est voulu —
            // le lore n'est pas gratuit, il occupe le temps qu'on passe avec sa communauté (§5.3).
        }
        // — 5. le commentaire quitte le Fil (sauf l'épinglé, §4.4) —
        if (t.reponse !== "epingler") {
            state.fil = fil(state).filter((c) => c.uid !== enFil.uid);
        }
        state.counters.fil_traites = (state.counters.fil_traites ?? 0) + 1;
        traites.push(effet);
    }
    // ⟦§4.5⟧ La troisième condition du malus d'abandon : traiter, c'est revenir.
    if (traites.length > 0)
        state.counters.fil_nuits_sans_lecture = 0;
    return { traites, cartes, hook, censure_posee: censurePosee };
}
// ── §4.2 `coeur` ─────────────────────────────────────────────────────────────
/**
 * ⟦§4.2 · A-113 §2 · A-117 §1⟧ « +Fidélité, +Hype. » Le nœud 💬 *Cœur sur vous* donne
 * **+50 % d'effet** (PLAN §4.14) — sur les deux jauges, pas seulement sur la Hype.
 *
 * ⚠️ **Sur `troll` / `toxique` : permis, et SANS EFFET** ⟦A-117 §1⟧. Mathieu : *« Le cœur sur
 * un toxique, tu peux le faire en vrai — ça signifierait du sarcasme. Mais c'est un layer
 * beaucoup trop fin, qui n'a pas besoin d'être géré. On peut ne pas l'empêcher, mais il faut
 * juste qu'il ne se passe rien. »* Le geste reste légal (`content/comments.json` ne le
 * retire d'aucune tonalité), le commentaire quitte quand même le Fil normalement (§4 point 5),
 * et le coût de lecture ⟦A-113 §2⟧ reste payé en amont par `coutSMTraitements` — seule cette
 * fonction-ci n'a plus rien à produire : ni Fidélité, ni Hype, ni le ×1,5 du nœud *Cœur sur
 * vous* (retour anticipé, aucune branche d'effet atteinte).
 *
 * ⚠️ **Plus de « si toxique, −SM ».** Avant ⟦A-113 §2⟧, répondre `coeur` à un `toxique`
 * doublait son coût de lecture (§4.1 d'origine, le « risque » que PLAN §4.14 mettait dans la
 * colonne du même nom). La lecture coûte désormais pareil quel que soit le geste
 * (`coutSMCommentaire`) : `coeur` n'est plus jamais plus cher qu'`ignorer`, il ajoute
 * seulement du positif — sauf sur `troll`/`toxique` depuis ⟦A-117 §1⟧, où il n'ajoute rien.
 *
 * Le gain de Fidélité passe par `appliquerTraitJauge` : c'est un **gain de jauge**, donc le
 * trait genré ⟦A-4 §2⟧ (×1,2 pour la créatrice) s'applique sans exception. C'est même le cœur
 * du trait — « la communauté-maison » se construit exactement là.
 */
function appliquerCoeur(state, content, effet) {
    if (TONALITES_DURES.includes(effet.tonalite))
        return; // ⟦A-117 §1⟧ — il ne se passe rien.
    const mult = capaciteDeLArbre(state, content, CAPACITE_COEUR_SUR_VOUS) ? MULT_COEUR_SUR_VOUS : 1;
    const fid = appliquerTraitJauge(state.genre, "loyalty", FID_COEUR * mult);
    state.loyalty += fid;
    effet.fidelite += fid;
    effet.hype += gagnerHype(state, HYPE_COEUR * mult, content);
}
// ── §4.3 `humour` ────────────────────────────────────────────────────────────
/**
 * ⟦§4.3⟧ **Le jet.** Un seul `draw` (`fil_outcome`, index = rang dans le lot).
 *
 * Réussi : Hype +5, image +1, et si la cible était un `troll` ou une `star`, le drapeau
 * `clip_viral_fil` — **+1 point de probabilité au jet viral de la prochaine publication**,
 * consommé là-bas (`publish.ts`). C'est le « parfois clip viral » de PLAN §4.14, exprimé avec
 * le mécanisme viral existant plutôt qu'avec un second tirage : un seul hasard à équilibrer.
 *
 * Raté : « tu t'es moqué d'un fan ». Fidélité −2, image −2, SM −2, Hype −3, et **aucun
 * `pending_event`** — c'est un *mini* bad buzz, il ne bloque pas le jeu (SPEC-MOTEUR §2.3).
 */
function appliquerHumour(state, content, seed, rang, effet) {
    const u = draw(seed, state.turn, "fil_outcome", rang);
    const reussi = u < probaHumour(state, content);
    effet.humour = reussi;
    if (reussi) {
        effet.hype += gagnerHype(state, HYPE_HUMOUR_REUSSI, content);
        state.channel_image_offset = (state.channel_image_offset ?? 0) + IMAGE_HUMOUR_REUSSI;
        effet.image += IMAGE_HUMOUR_REUSSI;
        if (effet.tonalite === "troll" || effet.tonalite === "star")
            state.flags.clip_viral_fil = true;
        return;
    }
    // Une PERTE de Fidélité : aucun trait de genre (`traits.ts` règle 1).
    state.loyalty += FID_HUMOUR_RATE;
    effet.fidelite += FID_HUMOUR_RATE;
    state.channel_image_offset = (state.channel_image_offset ?? 0) + IMAGE_HUMOUR_RATE;
    effet.image += IMAGE_HUMOUR_RATE;
    state.mental += SM_HUMOUR_RATE;
    effet.sm += SM_HUMOUR_RATE;
    effet.hype += gagnerHype(state, HYPE_HUMOUR_RATE, content);
}
// ── §4.4 `epingler` ──────────────────────────────────────────────────────────
/**
 * ⟦§4.4⟧ **L'amplificateur à double tranchant.** Épingler applique l'effet du commentaire ×2 —
 * et l'effet d'un commentaire, c'est sa tonalité. « Épingler un troll = amplifier le troll »
 * (PLAN §4.14). Ce ×2 est un effet de **Hype**, pas de SM : depuis ⟦A-113 §2⟧, épingler un dur
 * ne coûte plus double en lecture (`coutSMCommentaire` ne regarde plus la réponse) — seule sa
 * conséquence sur la Hype reste amplifiée.
 *
 * **Une seule épingle active** : épingler retire la précédente, *sans effet inverse* — ce qui
 * est fait est fait. Le commentaire dépunaisé retombe dans la file et reprend son
 * vieillissement normal : il n'était protégé que tant qu'il était affiché.
 */
function appliquerEpingle(state, content, cible, effet) {
    state.fil = fil(state).map((c) => c.uid === cible.uid ? { ...c, epingle: true } : c.epingle ? { ...c, epingle: false } : c);
    if (TONALITES_DURES.includes(cible.tonalite)) {
        effet.hype += gagnerHype(state, HYPE_EPINGLE_NEGATIVE * 2, content);
        return;
    }
    if (cible.tonalite === "sympa" || cible.tonalite === "star") {
        effet.hype += gagnerHype(state, HYPE_EPINGLE_POSITIVE * 2, content);
        const fid = appliquerTraitJauge(state.genre, "loyalty", FID_EPINGLE);
        state.loyalty += fid;
        effet.fidelite += fid;
    }
    // `question` / `critique` : épingler ne les amplifie ni dans un sens ni dans l'autre. Le
    // joueur les met en avant, le jeu n'en fait pas un événement — c'est un geste éditorial.
}
// ── §4.6 `supprimer / signaler` ──────────────────────────────────────────────
/**
 * ⟦§4.6 · A-113 §2⟧ **Neutraliser.** Le coût de SM de la tonalité (§4.1, `coutSMCommentaire` —
 * pas ici, ce n'est pas cette fonction qui touche `state.mental`) est payé **comme n'importe
 * quelle autre réponse** depuis ⟦A-113 §2⟧ : `supprimer` n'a plus de remise
 * (`RATIO_SM_SUPPRESSION` ⟦A-102 §1⟧, retiré — une moitié de prix n'a plus de sens quand tout
 * le monde paie le même prix). Aucun cadeau, aucun effet de tonalité. Rend `true` si
 * `fil_censure` vient de se poser.
 *
 * Chaque suppression est horodatée dans `counters.fil_suppressions`, et celle qui vise un
 * commentaire **ni `troll` ni `toxique`** y est inscrite **deux fois** : supprimer une critique
 * légitime, c'est ça, la censure. Un seul tableau porte donc à la fois la date et le poids —
 * une seconde liste à garder en phase aurait divergé.
 *
 * ⟦A-102 §2⟧ **Un `troll` ou un `toxique` supprimé n'y est plus inscrit du tout**
 * (`POIDS_SUPPRESSION_TROLL_TOXIQUE = 0`) : *« cette censure-là, personne ne la voit »*. La
 * boucle `for` ne pousse alors aucune date, ce qui revient à ne rien écrire — pas une entrée
 * de poids nul à filtrer plus tard, juste une absence.
 */
function appliquerSuppression(state, cible) {
    const poids = TONALITES_DURES.includes(cible.tonalite)
        ? POIDS_SUPPRESSION_TROLL_TOXIQUE
        : POIDS_SUPPRESSION_ABUSIVE;
    const suppressions = state.counters.fil_suppressions ?? [];
    for (let i = 0; i < poids; i++)
        suppressions.push(state.clock_h);
    state.counters.fil_suppressions = suppressions;
    return majCensure(state);
}
// ── §5.2 L'amorce d'événement ────────────────────────────────────────────────
/**
 * ⟦§5.2⟧ Armer le prochain tirage d'événement — l'effet `event_schedule` (SPEC-CONTENU §2.3)
 * avec `delai_h = 0`, donc **dû immédiatement**.
 *
 * C'est le « un gros créateur a commenté ?! » de PLAN §4.14, et c'est la prise du Fil sur les
 * PNJ persistants ⟦A-4 D⟧ : un commentaire `star` porte le `pnj_id`, l'événement qu'il amorce
 * parle du même PNJ.
 *
 * ⚠️ Le sidecar est initialisé ici plutôt qu'importé de `effects.ts` (`contentEffectsMutable`) :
 * `effects.ts` importe ce module depuis M3.22, la flèche inverse fermerait un cycle.
 */
function programmerEvenement(state, eventId) {
    state.content_effects ??= { modifiers: [], flags: {}, scheduled_events: [] };
    state.content_effects.scheduled_events.push({ event_id: eventId, due_h: state.clock_h });
}
//# sourceMappingURL=fil.js.map